import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Button, Toggle, Modal, Icon, P, Div } from "@drifthq/tide-core"
import { api } from 'modules/playbook/api'
import { track } from 'utils/trackingUtils'
import cx from 'classnames'
import config from 'config'
import SuccessLander from 'components/SuccessLander'
import PURPLE_ARROW_LEFT from './purple-arrow.svg'
import PURPLE_ARROW_RIGHT from './purple-arrow-2.svg'

import './style.css'

export const RefreshButton = ({ className, onRefresh }) => (
  <Div className={className} onClick={onRefresh}>
    <Icon className="bot-woohoo-refresh-icon" name="refresh-2" />
    Refresh preview
  </Div>
)

const STATUS_MAP = {
  false: "DISABLED",
  true: "ENABLED"
}

const PLAYBOOK_URL = `${config.CUSTOMER_WEB_URL}/playbooks`
const PLAYBOOK_REDIRECT_URL = ( playbookId ) => !!playbookId ? `${PLAYBOOK_URL}/editor/${playbookId}` : `${PLAYBOOK_URL}`

export const onExitOnboarding = (playbookId) => {
  window.location.replace(PLAYBOOK_REDIRECT_URL(playbookId)) //identifying user is handled by redirect to visual builder
  //if this redirect is changed to route to another step in the flow follow these steps: 1)window.drift.unload() 2)window.drift.init([ORG 1 embedId]) 3)identifyUser()
}

class BotWoohoo extends Component {

  static propTypes = {
    isWidgetInstalled: PropTypes.bool.isRequired,
    history: PropTypes.object.isRequired,
    path: PropTypes.string.isRequired,
    interactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    playbookId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    playbookType: PropTypes.string.isRequired,
    embedId: PropTypes.string.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      isBotOn: false,
      isModalOpen: false
    }
  }

  componentDidMount() {
    const { embedId, name, playbookId, playbookType } = this.props
    track(`${name} - Playbook Created`, { playbookId: playbookId, playbookName: playbookType, targeting: 'website-bot' })
    if (!embedId) {
      console.warn("Unable to load bot playbook - no embed id found")
      return
    }
    window.drift && window.drift.on('ready', () => {
      window.drift.unload()
      window.drift.init(embedId)
      setTimeout(this.refreshBot, 3000)
    })
  }

  refreshBot = () => {
    const { interactionId } = this.props
    window.drift.on('ready', (api) => {
      api.hideWelcomeMessage()
      api.startInteraction({ interactionId: interactionId })})
  }

  onTogglePlaybookStatus = async (id, status) => {
    const { playbookType } = this.props
    try {
      await api.updateStatus(id, status).toPromise()
      this.onPlaybookStatusToggled(id, status)
    } catch (err) {
      console.warn(err)
      track('Onboarding Playbook - Status Error', { playbookId: id, status: status, playbookName: playbookType })
    }
  }

  trackStatusToggle = () => {
    const { playbookType, segment } = this.props
    const { isBotOn } = this.state
    track('Onboarding Playbook - Status Toggled', { status: STATUS_MAP[isBotOn], playbookName: playbookType, segment: segment })
  }

  onPlaybookStatusToggled = () => {
    this.setState(prevState => ({
      isBotOn: !prevState.isBotOn
    }), this.trackStatusToggle)
  }

  onToggleModal = () => {
    this.setState(prevState => ({
      isModalOpen: !prevState.isModalOpen
    }))
  }

  onCloseModal = () => {
    this.setState({ isModalOpen: false })
  }

  onToggle = () => {
    const { playbookId } = this.props
    const { isBotOn } = this.state
    isBotOn ? this.onTogglePlaybookStatus(playbookId, STATUS_MAP[false]) : this.onToggleModal()
  }

  turnOn = () => {
    const { playbookId } = this.props
    this.onTogglePlaybookStatus(playbookId, STATUS_MAP[true])
    this.onCloseModal()
  }

  installDrift = () => {
    const { history, name, segment } = this.props
    track(`${name} Step - Back`, { route: '/bot/woohoo', segment: segment })
    window.drift.api.hideChat()
    history.push('/onboarding/bot-install')
  }

  MODAL_TEXT = Object.freeze({
    INSTALLED: {
      description:
        "Once you turn your bot on, it will appear on your website at the designated URL.",
      submitText: "Turn on",
      onSubmitClick: this.turnOn,
      cancelText: "Nevermind",
      onCancelClick: this.onCloseModal,
    },
    UNINSTALLED: {
      description:
        "Heads up! Your bot won’t appear at the designated URL until you install Drift on your site.",
      submitText: "Install Drift",
      onSubmitClick: this.installDrift,
      cancelText: "Turn on without installing",
      onCancelClick: this.turnOn,
    }
  })

  renderModal = () => {
    const { isModalOpen } = this.state
    const { isWidgetInstalled } = this.props
    const modalText = !isWidgetInstalled ? this.MODAL_TEXT.INSTALLED : this.MODAL_TEXT.UNINSTALLED
    return (
      <Modal open={isModalOpen} onClose={this.onCloseModal} className="turn-on-bot-modal" size="small">
        <Div className="turn-on-bot-modal-content">
          <Div className="turn-on-bot-modal-title">Turn your bot on?</Div>
          <P className="turn-on-bot-modal-description">{modalText.description}</P>
          <Div className="turn-on-bot-modal-buttons">
            <Button
              className="turn-on-bot-submit-button"
              onClick={modalText.onSubmitClick}>
              {modalText.submitText}
            </Button>
            <Button
              className="turn-on-bot-cancel-button"
              type="tertiary"
              onClick={modalText.onCancelClick}>
              {modalText.cancelText}
            </Button>
          </Div>
        </Div>
      </Modal>
    )
  }

  renderToggleBlock = () => {
    const { isBotOn } = this.state
    return (
      <Div className={cx("bot-woohoo-toggle-block", { 'bot-off': !isBotOn })} >
        <Div className="bot-woohoo-toggle-block-text">Your bot is {isBotOn ? "ON" : "OFF"}</Div>
        <Toggle
          className="bot-woohoo-toggle"
          checked={isBotOn}
          onChange={this.onToggle}
        />
      </Div>
    )
  }

  render() {
    const { playbookId } = this.props
    return <Fragment>
      <Div className="bot-woohoo-container">
        <Div className="bot-woohoo-left-panel">
          <SuccessLander
            className="bot-woohoo-success-lander"
            subheaderText={"Ready to turn it on?"}
            headerText={<Div>Woohoo! <br />Your bot is ready</Div>}
            buttonText="Customize my bot further"
            onButtonClick={() => onExitOnboarding(playbookId)}>
            <img className="bot-woohoo-tip-arrow-left" alt="tip-arrow-left" src={PURPLE_ARROW_LEFT} />
            {this.renderToggleBlock()}
          </SuccessLander>
        </Div>
        <Div className="bot-woohoo-right-panel">
          <Div className="bot-woohoo-tip-right">
            Preview your bot here!
            <img className="bot-woohoo-tip-arrow-right" alt="tip-arrow-right" src={PURPLE_ARROW_RIGHT} />
          </Div>
          <RefreshButton className="bot-woohoo-refresh" onRefresh={this.refreshBot} />
        </Div>
      </Div>
      {this.renderModal()}
    </Fragment>
  }
}

export default BotWoohoo


// WEBPACK FOOTER //
// ./src/layouts/OnboardingPlaybookWoohooStep/BotWoohoo/index.js